// Renders brand-configured content fields in the content submission form

import { type ContentField } from "@/lib/queries/content";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@repo/ui";
interface ContentFieldsFormProps {
  fields: ContentField[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
}

// Delimiter for MULTIPLE_SELECT stored values (unit separator, safe from user input)
const MULTI_SELECT_DELIMITER = "\x1F";

export function ContentFieldsForm({
  fields,
  values,
  onChange,
}: ContentFieldsFormProps) {
  if (fields.length === 0) return null;

  const handleFieldChange = (fieldId: string, value: string) => {
    onChange({ ...values, [fieldId]: value });
  };

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Additional Information</p>
      {fields.map((field: ContentField) => (
        <ContentFieldInput
          key={field.id}
          field={field}
          value={values[String(field.id)] ?? ""}
          onChange={(value: string) =>
            handleFieldChange(String(field.id), value)
          }
        />
      ))}
    </div>
  );
}

// ============================================================================
// Individual Field Renderers
// ============================================================================

interface ContentFieldInputProps {
  field: ContentField;
  value: string;
  onChange: (value: string) => void;
}

function ContentFieldInput({ field, value, onChange }: ContentFieldInputProps) {
  const fieldId = `content-field-${field.id}`;
  const label = field.isMandatory ? `${field.name} *` : field.name;

  switch (field.fieldType) {
    case "TEXT":
      return (
        <div>
          <Label htmlFor={fieldId}>{label}</Label>
          {field.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.description}
            </p>
          )}
          <Textarea
            id={fieldId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}...`}
            className="mt-1.5"
            rows={2}
          />
        </div>
      );

    case "NUMBER":
      return (
        <div>
          <Label htmlFor={fieldId}>{label}</Label>
          {field.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.description}
            </p>
          )}
          <Input
            id={fieldId}
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}...`}
            className="mt-1.5"
          />
        </div>
      );

    case "URL":
      return (
        <div>
          <Label htmlFor={fieldId}>{label}</Label>
          {field.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.description}
            </p>
          )}
          <Input
            id={fieldId}
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="mt-1.5"
          />
        </div>
      );

    case "DATE":
      return (
        <div>
          <Label htmlFor={fieldId}>{label}</Label>
          {field.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.description}
            </p>
          )}
          <Input
            id={fieldId}
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1.5"
          />
        </div>
      );

    case "SINGLE_SELECT":
      return (
        <div>
          <Label htmlFor={fieldId}>{label}</Label>
          {field.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.description}
            </p>
          )}
          <Select value={value || undefined} onValueChange={onChange}>
            <SelectTrigger id={fieldId} className="mt-1.5">
              <SelectValue
                placeholder={`Select ${field.name.toLowerCase()}...`}
              />
            </SelectTrigger>
            <SelectContent>
              {(field.selectOptions ?? []).map((option: string) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "MULTIPLE_SELECT":
      return (
        <div>
          <Label>{label}</Label>
          {field.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.description}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(field.selectOptions ?? []).map((option: string) => {
              const selected = value
                .split(MULTI_SELECT_DELIMITER)
                .filter(Boolean)
                .includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    const current = value
                      .split(MULTI_SELECT_DELIMITER)
                      .filter(Boolean);
                    const next = selected
                      ? current.filter((v: string) => v !== option)
                      : [...current, option];
                    onChange(next.join(MULTI_SELECT_DELIMITER));
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    selected
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-input hover:bg-accent"
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      );

    default:
      return (
        <div>
          <Label htmlFor={fieldId}>{label}</Label>
          <Input
            id={fieldId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}...`}
            className="mt-1.5"
          />
        </div>
      );
  }
}
